import { create } from "zustand";
import type { User } from "../types";
import { logout as logoutApi } from "../api/auth";
import { bootstrapRefresh, silentRefresh } from "../api/refresh";
import { tokenStore } from "../auth/tokenStore";
import { authIdentity, extractUserFromToken } from "../utils/token";
import { clearSessionCache } from "../app/queryClient";
import { useBusinessUnitStore } from "./businessUnit";
import {
  blockAutomaticRecovery,
  clearRecoveryBlock,
  automaticRecoveryBlocked,
} from "../auth/recovery";

const authChannel =
  typeof BroadcastChannel === "undefined"
    ? null
    : new BroadcastChannel("pw-cms-auth");

function broadcastAuthChange(): void {
  // Send an invalidation event, never bearer credentials or profile data.
  authChannel?.postMessage({ changed: true });
  safeSetItem("pw-auth-changed", crypto.randomUUID());
}

interface AuthState {
  user: User | null;
  token: string | null;
  loading: boolean;
  login: (token: string, user: User) => Promise<void>;
  logout: () => Promise<boolean>;
  forceLogout: (broadcast?: boolean) => void;
  initialize: () => void;
}

function safeSetItem(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // QuotaExceededError or private browsing — silently fail
  }
}

function safeRemoveItem(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // Silently fail
  }
}

function removeLegacyTokenFromUrl(): void {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has("token")) return;
    url.searchParams.delete("token");
    window.history.replaceState({}, "", url.toString());
  } catch {
    // URL cleanup is best-effort; URL tokens are never used to authenticate.
  }
}

function matchesStoredProfile(user: User): boolean {
  try {
    const stored = JSON.parse(
      localStorage.getItem("user") || "null",
    ) as Partial<User> | null;
    return (
      stored?.userId === user.userId &&
      stored?.companyId === user.companyId &&
      stored?.role === user.role
    );
  } catch {
    return false;
  }
}

function applyMemoryToken(
  set: (partial: Partial<AuthState>) => void,
  token: string,
): User | null {
  const userData = extractUserFromToken(token);
  if (userData) {
    const previousIdentity = authIdentity(tokenStore.get());
    if (previousIdentity !== authIdentity(token)) {
      clearSessionCache();
      // Reload starts without an in-memory token. Preserve that user's saved
      // facility only when the recovered JWT matches the previous profile.
      // Stored profile data scopes this preference; it never authenticates users.
      if (previousIdentity !== null || !matchesStoredProfile(userData)) {
        useBusinessUnitStore.getState().reset();
      }
      safeRemoveItem("gm-ai-chat");
    }
    tokenStore.set(token);
    safeSetItem("user", JSON.stringify(userData));
    set({ token, user: userData });
    return userData;
  }
  return null;
}

// Keep the refresh client's "token refreshed" result in sync with the store
// (silent rotation triggered by the axios interceptor happens off the React tree).
function registerRefreshListener(
  set: (partial: Partial<AuthState>) => void,
): void {
  window.addEventListener("auth:token-refreshed", (event) => {
    const detail = (event as CustomEvent<{ token?: string }>).detail;
    if (detail?.token) {
      applyMemoryToken(set, detail.token);
    }
  });
}

export const useAuthStore = create<AuthState>((set) => {
  if (typeof window !== "undefined") {
    registerRefreshListener(set);
  }

  return {
    user: null,
    token: null,
    loading: true,

    login: async (token: string, user: User) => {
      // The original issuance token is used only for this exchange. It is
      // never placed in the shared request interceptor or application state.
      tokenStore.clear();
      clearSessionCache();
      safeRemoveItem("user");
      safeRemoveItem("pw-refresh-request");
      safeRemoveItem("gm-ai-chat");
      useBusinessUnitStore.getState().reset();
      set({ token: null, user: null });
      const generation = tokenStore.generation();
      const fresh = await bootstrapRefresh(token);
      if (!fresh || tokenStore.generation() !== generation) {
        throw new Error(
          "Unable to establish a secure session. Please sign in again.",
        );
      }
      // Drop any cached data left behind by a previous session so the new
      // user never sees stale cross-account data (e.g. dashboard/entity cache).
      clearSessionCache();
      const canonical = extractUserFromToken(fresh);
      if (!canonical || !authIdentity(fresh)) {
        throw new Error("Invalid session identity. Please sign in again.");
      }
      const accepted = {
        ...canonical,
        displayName: user.displayName || canonical.displayName,
      };
      clearRecoveryBlock();
      tokenStore.set(fresh);
      safeSetItem("user", JSON.stringify(accepted));
      set({ token: fresh, user: accepted });
      broadcastAuthChange();
    },

    logout: async () => {
      try {
        await logoutApi();
      } catch {
        return false;
      }
      clearSessionCache();
      tokenStore.clear();
      useBusinessUnitStore.getState().reset();
      safeRemoveItem("user");
      safeRemoveItem("token");
      safeRemoveItem("pw-refresh-request");
      try {
        localStorage.removeItem("gm-ai-chat");
      } catch {
        // ignore
      }
      set({ token: null, user: null });
      broadcastAuthChange();
      return true;
    },

    forceLogout: (broadcast = true) => {
      // A still-valid cookie must not silently sign us back in after a protected
      // API rejects the session. Persist only this non-secret flag across reloads.
      blockAutomaticRecovery();
      clearSessionCache();
      useBusinessUnitStore.getState().reset();
      safeRemoveItem("token");
      safeRemoveItem("user");
      safeRemoveItem("pw-refresh-request");
      tokenStore.clear();
      try {
        localStorage.removeItem("gm-ai-chat");
      } catch {
        // ignore
      }
      set({ token: null, user: null });
      if (broadcast) broadcastAuthChange();
    },

    initialize: async () => {
      try {
        removeLegacyTokenFromUrl();
        if (automaticRecoveryBlocked()) {
          tokenStore.clear();
          set({ token: null, user: null, loading: false });
          return;
        }
        const generation = tokenStore.generation();
        // The HttpOnly refresh cookie is the only persisted credential.
        const refreshed = await silentRefresh();
        if (generation !== tokenStore.generation() && !refreshed) {
          // A login began while reload recovery was in flight.
          set({ loading: false });
          return;
        }
        if (refreshed && applyMemoryToken(set, refreshed)) {
          set({ loading: false });
          return;
        }
        tokenStore.clear();
        safeRemoveItem("token");
        safeRemoveItem("user");
        set({ token: null, user: null, loading: false });
      } catch {
        tokenStore.clear();
        safeRemoveItem("token");
        safeRemoveItem("user");
        set({ token: null, user: null, loading: false });
      }
    },
  };
});

if (authChannel) {
  authChannel.onmessage = () => {
    // The shared cookie may now belong to another session. Cancel use of the
    // old identity immediately; a reload/login can recover the current cookie.
    useAuthStore.getState().forceLogout(false);
  };
}
if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    if (event.key === "pw-auth-changed")
      useAuthStore.getState().forceLogout(false);
  });
}
